import { isJavaDigit, isJavaWhitespace, javaDoubleToString } from '../javaText.ts';

/**
 * The desktop's `interchange.JsonReader`, for importing tables (and later script packs) with
 * the same results and the same messages: objects as `Map`s (their keys in order), whole
 * numbers as longs and others as doubles - which matters, since `String.valueOf` writes
 * 5 and 5.0 differently - plus strings, booleans and null.
 */

export class InterchangeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'InterchangeError';
  }
}

/** A JSON number as Java reads it: a `Long` when it's whole, a `Double` otherwise. */
export class JavaNumber {
  constructor(
    readonly value: number,
    readonly isLong: boolean,
    readonly long: bigint = BigInt(Math.trunc(value)),
  ) {}
  /** `String.valueOf`. */
  toString(): string {
    return this.isLong ? String(this.long) : javaDoubleToString(this.value);
  }
}

export type JavaJson = string | boolean | null | JavaNumber | JavaJson[] | Map<string, JavaJson>;

/** `String.valueOf(value)` for anything the reader makes. */
export function javaValueOf(value: JavaJson): string {
  if (value === null) return 'null';
  if (value instanceof Map) return `{${[...value].map(([k, v]) => `${k}=${javaValueOf(v)}`).join(', ')}}`;
  if (Array.isArray(value)) return `[${value.map(javaValueOf).join(', ')}]`;
  return String(value);
}

const LONG_MIN = -(2n ** 63n);
const LONG_MAX = 2n ** 63n - 1n;

class Reader {
  pos = 0;
  constructor(readonly text: string) {}

  readValue(): JavaJson {
    if (this.pos >= this.text.length) throw this.error('The JSON document ends too early');
    const c = this.text[this.pos]!;
    switch (c) {
      case '{':
        return this.readObject();
      case '[':
        return this.readArray();
      case '"':
        return this.readString();
      case 't':
        return this.readLiteral('true', true);
      case 'f':
        return this.readLiteral('false', false);
      case 'n':
        return this.readLiteral('null', null);
      default:
        if (c === '-' || isJavaDigit(c)) return this.readNumber();
        throw this.error(`Unexpected character '${c}'`);
    }
  }

  readObject(): Map<string, JavaJson> {
    const map = new Map<string, JavaJson>();
    this.pos++;
    this.skipWhitespace();
    if (this.peek() === '}') {
      this.pos++;
      return map;
    }
    for (;;) {
      this.skipWhitespace();
      if (this.peek() !== '"') throw this.error('Expected a quoted property name');
      const key = this.readString();
      this.skipWhitespace();
      this.expect(':');
      this.skipWhitespace();
      map.set(key, this.readValue());
      this.skipWhitespace();
      const next = this.peek();
      this.pos++;
      if (next === '}') return map;
      if (next !== ',') {
        this.pos--;
        throw this.error("Expected ',' or '}'");
      }
    }
  }

  readArray(): JavaJson[] {
    const list: JavaJson[] = [];
    this.pos++;
    this.skipWhitespace();
    if (this.peek() === ']') {
      this.pos++;
      return list;
    }
    for (;;) {
      this.skipWhitespace();
      list.push(this.readValue());
      this.skipWhitespace();
      const next = this.peek();
      this.pos++;
      if (next === ']') return list;
      if (next !== ',') {
        this.pos--;
        throw this.error("Expected ',' or ']'");
      }
    }
  }

  readString(): string {
    this.pos++;
    let out = '';
    while (this.pos < this.text.length) {
      const c = this.text[this.pos++]!;
      if (c === '"') return out;
      if (c !== '\\') {
        out += c;
        continue;
      }
      if (this.pos >= this.text.length) break;
      const escape = this.text[this.pos++]!;
      switch (escape) {
        case '"':
        case '\\':
        case '/':
          out += escape;
          break;
        case 'b':
          out += '\b';
          break;
        case 'f':
          out += '\f';
          break;
        case 'n':
          out += '\n';
          break;
        case 'r':
          out += '\r';
          break;
        case 't':
          out += '\t';
          break;
        case 'u': {
          if (this.pos + 4 > this.text.length) throw this.error('Incomplete \\u escape');
          const hex = this.text.slice(this.pos, this.pos + 4);
          // Integer.parseInt(hex, 16): an optional sign, then hex digits.
          if (!/^[+-]?[0-9a-fA-F]+$/.test(hex)) throw this.error('Invalid \\u escape');
          out += String.fromCharCode(parseInt(hex, 16) & 0xffff);
          this.pos += 4;
          break;
        }
        default:
          throw this.error(`Invalid escape '\\${escape}'`);
      }
    }
    throw this.error('A string is missing its closing quote');
  }

  readNumber(): JavaNumber {
    const start = this.pos;
    if (this.peek() === '-') this.pos++;
    while (this.pos < this.text.length && '0123456789.eE+-'.includes(this.text[this.pos]!)) this.pos++;
    const number = this.text.slice(start, this.pos);
    const invalid = () => {
      this.pos = start;
      return this.error(`Invalid number '${number}'`);
    };
    if (number.includes('.') || number.includes('e') || number.includes('E')) {
      // Double.parseDouble, for the characters a number may hold here.
      if (!/^[+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?$/.test(number)) throw invalid();
      return new JavaNumber(Number(number), false);
    }
    // Long.parseLong.
    if (!/^[+-]?\d+$/.test(number)) throw invalid();
    const long = BigInt(number);
    if (long < LONG_MIN || long > LONG_MAX) throw invalid();
    return new JavaNumber(Number(long), true, long);
  }

  readLiteral<T>(literal: string, value: T): T {
    if (!this.text.startsWith(literal, this.pos)) throw this.error('Unexpected text');
    this.pos += literal.length;
    return value;
  }

  expect(c: string): void {
    if (this.peek() !== c) throw this.error(`Expected '${c}'`);
    this.pos++;
  }

  peek(): string {
    return this.pos < this.text.length ? this.text[this.pos]! : '\0';
  }

  skipWhitespace(): void {
    while (this.pos < this.text.length && isJavaWhitespace(this.text[this.pos]!)) this.pos++;
  }

  error(message: string): InterchangeError {
    let line = 1;
    let column = 1;
    for (let i = 0; i < Math.min(this.pos, this.text.length); i++) {
      if (this.text[i] === '\n') {
        line++;
        column = 1;
      } else column++;
    }
    return new InterchangeError(`Not valid JSON: ${message} (line ${line}, column ${column})`);
  }
}

/** `JsonReader.parse`. */
export function readJavaJson(text: string): JavaJson {
  const reader = new Reader(text.startsWith('﻿') ? text.slice(1) : text);
  reader.skipWhitespace();
  const value = reader.readValue();
  reader.skipWhitespace();
  if (reader.pos < reader.text.length) throw reader.error('Unexpected text after the end of the JSON document');
  return value;
}
